import { z } from 'zod';
import { ErrorResponseSchema } from '../../common/base';

/**
 * Request schema for wishlist actions
 * POST /api/user/wishlist
 *
 * Supports three actions: watch, unwatch, get
 */
export const WishlistRequestSchema = z.discriminatedUnion('action', [
  // Watch action - add listing to wishlist
  z.object({
    action: z.literal('watch'),
    listingId: z.union([z.string(), z.number()]).transform(val => String(val)),
  }),
  // Unwatch action - remove listing from wishlist
  z.object({
    action: z.literal('unwatch'),
    listingId: z.union([z.string(), z.number()]).transform(val => String(val)),
  }),
  // Get action - retrieve all watched listings
  z.object({
    action: z.literal('get'),
    listingId: z.union([z.string(), z.number()]).transform(val => String(val)).optional(),
  }),
]);

export type WishlistRequest = z.infer<typeof WishlistRequestSchema>;

/**
 * Legacy schema for adding item to wishlist (backwards compatibility)
 * @deprecated Use WishlistRequestSchema instead
 */
export const AddToWishlistRequestSchema = z.object({
  /** ID of the listing to add to wishlist */
  listingId: z.union([z.string(), z.number()]).transform(val => String(val)),
});

export type AddToWishlistRequest = z.infer<typeof AddToWishlistRequestSchema>;

/**
 * Wishlist item user schema
 */
const WishlistItemUserSchema = z.object({
  id: z.string(),
  username: z.string().nullable().optional(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  trustLevel: z.number().nullable().optional(),
});

/**
 * Wishlist item photo schema
 */
const WishlistItemPhotoSchema = z.object({
  url: z.string(),
  thumbUrl: z.string(),
  '320x320Url': z.string().optional(),
});

/**
 * Wishlist item schema
 * Contains the listing details and wishlist metadata
 */
export const WishlistItemSchema = z.object({
  /** Listing ID as string */
  id: z.string(),
  /** Listing title */
  title: z.string().nullable(),
  /** Listing price */
  price: z.union([z.string(), z.number()]).nullable(),
  /** Listing status */
  status: z.number().nullable(),
  /** Whether listing has photo */
  hasPhoto: z.number(),
  /** Data source ID for external datasources */
  dataSourceId: z.number().optional(),
  /** Photo thumbnail URL */
  photo: z.string().nullable(),
  /** Array of photo objects */
  photos: z.array(WishlistItemPhotoSchema),
  /** Category ID */
  categoryId: z.number().nullable(),
  /** Category group ID */
  categoryGroupId: z.number().nullable(),
  /** Whether listing is shippable */
  shippable: z.number().nullable(),
  /** Location text */
  locationText: z.string().nullable(),
  /** Date listing was created */
  createdDate: z.coerce.date().nullable(),
  /** Date item was added to wishlist */
  watchedDate: z.coerce.date().nullable(),
  /** Remote URL for external deals */
  remote_url: z.string().nullable().optional(),
  /** User who created the listing */
  user: WishlistItemUserSchema.nullable(),
  /** Coupon discount fields (auto-applied) */
  couponName: z.string().nullable().optional(),
  couponDiscountAmount: z.number().nullable().optional(),
  slashedPrice: z.string().nullable().optional(),
  percentOff: z.number().nullable().optional(),
});

export type WishlistItem = z.infer<typeof WishlistItemSchema>;

/**
 * Success response schema for watch/unwatch actions
 */
const WishlistActionSuccessSchema = z.object({
  success: z.literal(true),
});

/**
 * Success response schema for get action
 * Returns array of wishlist items with full listing details
 */
export const WishlistGetSuccessSchema = z.object({
  success: z.literal(true),
  /** Array of wishlist items with full listing details */
  watches: z.array(WishlistItemSchema),
});

export type WishlistSuccess = z.infer<typeof WishlistGetSuccessSchema>;

/**
 * Response schema for watch/unwatch actions
 * Uses discriminatedUnion on 'success' field
 */
export const WishlistActionResponseSchema = z.discriminatedUnion('success', [
  WishlistActionSuccessSchema,
  ErrorResponseSchema,
]);

export type WishlistActionResponse = z.infer<typeof WishlistActionResponseSchema>;

/**
 * Response schema for get action
 * Uses discriminatedUnion on 'success' field
 */
export const WishlistGetResponseSchema = z.discriminatedUnion('success', [
  WishlistGetSuccessSchema,
  ErrorResponseSchema,
]);

export type WishlistGetResponse = z.infer<typeof WishlistGetResponseSchema>;

/**
 * Combined response schema for wishlist endpoints
 * Uses z.union to handle multiple success shapes
 *
 * Used for both:
 * - GET /api/user/wishlist (retrieve wishlist)
 * - POST /api/user/wishlist (add/remove from wishlist)
 *
 * @example
 * ```typescript
 * const response = WishlistResponseSchema.parse(data);
 * if (response.success) {
 *   if ('watches' in response) {
 *     response.watches.forEach(item => {
 *       console.log('Listing:', item.title);
 *     });
 *   }
 * } else {
 *   console.error('Error:', response.error);
 * }
 * ```
 */
export const WishlistResponseSchema = z.union([
  WishlistActionSuccessSchema,
  WishlistGetSuccessSchema,
  ErrorResponseSchema,
]);

export type WishlistResponse = z.infer<typeof WishlistResponseSchema>;
